import React from 'react';
import { motion } from 'motion/react';
import { ShieldCheck, Activity, Wind, LifeBuoy, Zap, Ruler } from 'lucide-react';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useBrand } from '../context/BrandContext';

const DEFAULT_GEAR = {
  day: [
    { item: "High-Viz PFD", desc: "USCG/TC approved Level 70 buoyancy.", image: "https://picsum.photos/seed/pfd/400/400" },
    { item: "6ft Leash", desc: "Coiled for zero-drag, high-tensile strength.", image: "https://picsum.photos/seed/leash/400/400" },
    { item: "2m Tow Rope", desc: "Essential for emergency recovery and stabilization.", image: "https://picsum.photos/seed/rope/400/400" },
    { item: "Visual Signal", desc: "Pealess whistle and signaling mirror.", image: "https://picsum.photos/seed/whistle/400/400" },
    { item: "Hydration", desc: "3L coiled bladder for endurance missions.", image: "https://picsum.photos/seed/water/400/400" }
  ],
  night: [
    { item: "Nav Lights (Red/Green)", desc: "Port (Red) and Starboard (Green) for orientation.", image: "https://picsum.photos/seed/nav/400/400" },
    { item: "Stern Light (White)", desc: "360° white light visible for 2 miles on back.", image: "https://picsum.photos/seed/stern/400/400" },
    { item: "Coiled Headlight", desc: "High-lumen waterproof headlamp for line-of-sight.", image: "https://picsum.photos/seed/headlight/400/400" },
    { item: "PFD & Leash", desc: "Night-visible reflective components.", image: "https://picsum.photos/seed/night_pfd/400/400" },
    { item: "2m Rope & Whistle", desc: "Coiled auditory and physical safety links.", image: "https://picsum.photos/seed/night_rope/400/400" }
  ]
};

const SafetyFirst: React.FC = () => {
  const { assets, resolveMediaUrl } = useBrand();
  const [activeMode, setActiveMode] = React.useState<'day' | 'night'>('day');
  const [gear, setGear] = React.useState(DEFAULT_GEAR);

  React.useEffect(() => {
    const q = query(collection(db, 'safety_gear'), orderBy('order', 'asc'));
    const unsub = onSnapshot(q, (snap) => {
      if (!snap.empty) {
        const fetchedItems = snap.docs.map(d => d.data());
        setGear({
          day: fetchedItems.filter((i: any) => i.category === 'day') as any,
          night: fetchedItems.filter((i: any) => i.category === 'night') as any
        });
      }
    });
    return unsub;
  }, []);

  return (
    <section className="py-32 bg-ows-white relative overflow-hidden" id="safety">
      <div className="max-w-7xl mx-auto px-4 relative">
        <div className="text-center mb-24">
          <span className="text-[10px] font-mono font-bold uppercase tracking-[0.4em] text-ows-teal">MISSION_CRITICAL</span>
          <h2 className="text-4xl md:text-7xl font-display font-light uppercase tracking-tight mt-2 mb-8 italic-serif">
            NAVIGATION <br />
            <span className="text-ows-teal">PROTOCOLS.</span>
          </h2>
          <p className="text-lg text-[#141414]/60 max-w-2xl mx-auto font-mono uppercase tracking-tighter leading-relaxed">
            The open water doesn't forgive ignorance. We educate our members on the technical requirements of survival, from daylight navigation to the high-stakes discipline of night crossings.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-start">
          <div className="space-y-12">
            <div className="flex gap-4 p-2 bg-ows-sand/50 rounded-2xl w-fit">
              <button 
                onClick={() => setActiveMode('day')}
                className={`px-8 py-3 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all ${activeMode === 'day' ? 'bg-ows-teal text-white shadow-lg' : 'text-[#141414]/40 hover:text-ows-teal'}`}
              >
                Daylight Op
              </button>
              <button 
                onClick={() => setActiveMode('night')}
                className={`px-8 py-3 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all ${activeMode === 'night' ? 'bg-[#141414] text-white shadow-lg' : 'text-[#141414]/40 hover:text-ows-teal'}`}
              >
                Night Raid
              </button>
            </div>

            <div className="space-y-10">
              <h3 className="text-2xl font-display font-light uppercase tracking-widest text-[#141414]">
                {activeMode === 'day' ? "Essential Day Gear" : "Critical Night Equipment"}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                {gear[activeMode].map((item, idx) => (
                  <motion.div 
                    key={`${activeMode}-${idx}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="group/item flex flex-col sm:flex-row items-start sm:items-center gap-4"
                  >
                    <div className="h-20 w-20 shrink-0 bg-ows-sand/20 rounded-2xl overflow-hidden mb-2 sm:mb-0 border border-[#141414]/5">
                      <img 
                        src={resolveMediaUrl(item.image)} 
                        alt={item.item} 
                        className="w-full h-full object-cover group-hover/item:scale-110 transition-transform duration-700" 
                        referrerPolicy="no-referrer"
                      />
                    </div>
                    <div className="space-y-1 border-l-2 border-ows-teal/20 pl-4 w-full">
                      <h4 className="text-base font-mono font-bold uppercase tracking-widest text-[#141414]">{item.item}</h4>
                      <p className="text-sm font-mono text-[#141414]/60 uppercase tracking-tighter leading-tight">
                        {item.desc}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-8 bg-[#141414] rounded-[2rem] text-white overflow-hidden relative group flex flex-col justify-between">
                <div className="absolute top-0 right-0 p-8 opacity-10">
                  <ShieldCheck className="h-24 w-24" />
                </div>
                <div>
                  <h4 className="text-lg font-display uppercase tracking-widest mb-4">The Golden Rule</h4>
                  <p className="text-[11px] font-mono uppercase tracking-tighter leading-relaxed text-white/60">
                    "Always file a float plan. Never leave the shore without a redundant communication line. The Syndicate baseline is survival first, expedition second."
                  </p>
                </div>
              </div>

              <a 
                href="https://cert.openwatersyndicate.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="p-8 bg-ows-teal rounded-[2rem] text-white overflow-hidden relative group flex flex-col justify-between hover:scale-[1.02] hover:shadow-xl transition-all duration-300 pointer-events-auto cursor-pointer block"
              >
                <div className="absolute top-0 right-0 p-8 opacity-15 group-hover:scale-110 transition-transform duration-500">
                  <LifeBuoy className="h-24 w-24 text-white" />
                </div>
                <div className="space-y-3 z-10">
                  <span className="inline-block text-[8px] font-mono font-bold uppercase tracking-[0.3em] bg-white/20 text-white px-2.5 py-1 rounded-full">
                    CERTIFICATION SYSTEM
                  </span>
                  <div>
                    <h4 className="text-lg font-display uppercase tracking-widest mb-2 text-white">Get Certified</h4>
                    <p className="text-[11px] font-mono uppercase tracking-tighter leading-relaxed text-white/80">
                      Master maritime safety, emergency drills, and navigation. Earn credentials recognized across the syndicate.
                    </p>
                  </div>
                </div>
                <div className="mt-6 flex items-center gap-2 text-[10px] font-mono font-bold uppercase tracking-widest text-[#141414] bg-white rounded-xl px-5 py-2.5 w-fit z-10">
                  <span>Accreditation Portal</span>
                  <span className="group-hover:translate-x-1 transition-transform duration-300">→</span>
                </div>
              </a>
            </div>
          </div>

          <div className="relative">
            <motion.div 
              key={activeMode}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              className="aspect-square bg-ows-sand rounded-[40px] overflow-hidden shadow-2xl relative border border-[#141414]/5"
            >
              <video 
                src={activeMode === 'day' 
                    ? (resolveMediaUrl(assets.daySafetyVideo) || "https://cdn.pixabay.com/video/2019/04/14/22751-329623861_large.mp4") 
                    : (resolveMediaUrl(assets.nightSafetyVideo) || "https://cdn.pixabay.com/video/2021/08/17/85375-588820005_large.mp4")} 
                autoPlay 
                loop 
                muted 
                playsInline
                className={`w-full h-full object-cover transition-all duration-1000 ${activeMode === 'night' ? 'brightness-75' : 'brightness-100'}`}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#141414]/60 via-transparent to-transparent flex items-end p-12">
                <div className="flex items-center gap-6">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${activeMode === 'day' ? 'bg-ows-teal' : 'bg-ows-orange shadow-[0_0_20px_rgba(255,140,66,0.5)]'}`}>
                    {activeMode === 'day' ? <ShieldCheck className="h-6 w-6 text-white" /> : <Zap className="h-6 w-6 text-white" />}
                  </div>
                  <div>
                    <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-white/40 block mb-1">Navigation System</span>
                    <h4 className="text-xl font-display uppercase tracking-widest text-white">
                      {activeMode === 'day' ? 'Visual Baseline' : 'Illumination Protocol'}
                    </h4>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SafetyFirst;
